import React, { useState } from 'react';
import styled from 'styled-components';

// ── Styles ──────────────────────────────────────────────────────────────────
const Page = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  padding: 48px 56px;
  font-family: system-ui, -apple-system, sans-serif;
`;
const H1 = styled.h1`font-size: 22px; font-weight: 700; color: #1A2744; margin: 0 0 4px;`;
const Subtitle = styled.p`font-size: 14px; color: #64748b; margin: 0 0 32px;`;

// ── Tabs ────────────────────────────────────────────────────────────────────
const TabBar = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 32px;
  border-bottom: 2px solid #e2eaf4;
  padding-bottom: 0;
`;
const Tab = styled.button<{ $active: boolean }>`
  padding: 10px 20px;
  border: none;
  border-bottom: 3px solid ${p => p.$active ? '#1A2744' : 'transparent'};
  background: none;
  color: ${p => p.$active ? '#1A2744' : '#64748b'};
  font-size: 14px;
  font-weight: ${p => p.$active ? '700' : '500'};
  cursor: pointer;
  margin-bottom: -2px;
  transition: all 0.15s;
  white-space: nowrap;
  &:hover { color: #1A2744; }
`;

const DemoCard = styled.div`
  background: #fff;
  border: 1px solid #e2eaf4;
  border-radius: 16px;
  padding: 32px 36px;
`;
const DemoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 28px;
`;
const DemoId = styled.div`
  background: #1A2744;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: 6px;
`;
const DemoTitle = styled.h2`font-size: 18px; font-weight: 700; color: #1A2744; margin: 0;`;
const YoutubeLink = styled.a`
  font-size: 13px;
  color: #dc2626;
  font-weight: 600;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  &:hover { text-decoration: underline; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 24px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;
const InfoBlock = styled.div`
  background: #f8fafc;
  border: 1px solid #e2eaf4;
  border-radius: 10px;
  padding: 16px 20px;
`;
const BlockLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  margin-bottom: 8px;
`;
const BlockValue = styled.div`
  font-size: 13px;
  color: #1e293b;
  font-family: 'Menlo', 'Monaco', monospace;
  line-height: 1.6;
  word-break: break-all;
`;
const ClipGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
  gap: 6px;
  margin-top: 6px;
`;
const ClipBadge = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  padding: 4px 6px;
  font-size: 11px;
  font-weight: 600;
  color: #1d4ed8;
  text-align: center;
`;

const Divider = styled.hr`border: none; border-top: 1px solid #e2eaf4; margin: 24px 0;`;

const CopyRow = styled.div`display: flex; align-items: flex-start; gap: 10px;`;
const CopyField = styled.div`
  flex: 1;
  background: #f1f5f9;
  border: 1px solid #e2eaf4;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 13px;
  color: #1e293b;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
`;
const EmailCopyField = styled.div`
  flex: 1;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 20px 24px;
  font-size: 15px;
  color: #1e293b;
  line-height: 1.8;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: 'Google Sans', 'Helvetica Neue', Arial, sans-serif;
  max-height: 400px;
  overflow-y: auto;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.04);
`;
const CopyBtn = styled.button<{ $copied: boolean }>`
  flex-shrink: 0;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid ${p => p.$copied ? '#16a34a' : '#cbd5e1'};
  background: ${p => p.$copied ? '#f0fdf4' : '#fff'};
  color: ${p => p.$copied ? '#16a34a' : '#64748b'};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  &:hover { background: ${p => p.$copied ? '#f0fdf4' : '#f8fafc'}; }
`;
const FieldLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #94a3b8;
  margin-bottom: 6px;
  margin-top: 16px;
`;
const SectionTitle = styled.h3`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  margin: 0 0 14px;
`;

// ── Copy button helper ───────────────────────────────────────────────────────
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <CopyBtn $copied={copied} onClick={handle}>{copied ? '✓ Copied' : 'Copy'}</CopyBtn>;
}

// ── Demo data ────────────────────────────────────────────────────────────────
const DEMOS = [
  {
    id: 'video_demo_1',
    label: 'Video Demo 1',
    youtubeUrl: 'https://www.youtube.com/watch?v=D8mgWmshhiw',
    totalClips: 14,
    clipNums: Array.from({ length: 14 }, (_, i) => i + 1),
    scriptsDir: 'artifacts/video_demo_scripts/video_demo_1/scripts/',
    mp3Dir: 'artifacts/video_demo_scripts/video_demo_1/mp3/',
    rawVideo: 'output/video_demo/video_demo_1_raw.mp4',
    captionCmd: 'python3 artifacts/video_demo_scripts/add_captions.py video_demo_1\n  output/video_demo/video_demo_1_raw.mp4\n  output/video_demo/video_demo_1.mp4',
    finalVideo: 'output/video_demo/video_demo_1.mp4',
    redact: {
      annotationsFile: 'output/video_demo/video_demo_1_blur_annotations.json',
      redactedVideo: 'output/video_demo/video_demo_1_redacted.mp4',
      blurCmd: 'python3 artifacts/video_demo_scripts/add_blur.py output/video_demo/video_demo_1_blur_annotations.json output/video_demo/video_demo_1_raw.mp4 output/video_demo/video_demo_1_redacted.mp4',
    },
    tts: {
      voice: 'Christopher — Warm, Deep, and Impactful',
      voiceId: 'rUwfJCzlNVSupX1xyzzX',
      model: 'eleven_multilingual_v2',
      stability: 0.50,
      similarityBoost: 0.59,
      style: 0.94,
      speakerBoost: false,
      generateCmd: 'python3 artifacts/video_demo_scripts/generate_mp3_clips.py video_demo_1',
    },
    marketing: {
      title: 'Our AI Agent Just Replaced Hours of Manual Clinic Work',
      shortDesc:
        'Logging in, scrolling, clicking, downloading, organizing — our AI agent does all of it. Watch our MCP-powered agent automatically collect lab results and PDF test reports from patient portals like Vibrant Wellness and Rupa Health. No copy-paste. No manual downloading. Just one click, and your data is ready.',
      email: `Subject: Automated Lab Collection for Vibrant Wellness & Rupa Health — Now Live

Dear Clinic Team,

Watch the demo here: https://www.youtube.com/watch?v=D8mgWmshhiw

We're excited to share a new capability we've just shipped: our AI agent can now automatically log into patient lab portals, collect test results, and download PDF reports, completely hands-free.

The problem it solves

Clinics spend significant time each week on repetitive data tasks: logging into third-party lab portals, manually downloading patient PDFs page by page, organizing reports, and moving files around. For larger practices with hundreds of patients, this can consume hours of staff time every week, time that should be spent on patient care.

What the agent does

Once connected, our AI agent handles the entire workflow on your behalf:
• Logs into your lab portal using your own credentials on your own device, nothing leaves your machine
• Scans your full patient roster and counts available records
• Automatically downloads all test results and PDF documents, page by page
• Organizes everything into clean local folders, ready for clinical review and import

No manual clicking. No copy-paste. No file management.

Portals supported today
• Vibrant Wellness, specialty blood panels
• Rupa Health, functional medicine lab tests

We are actively adding new portal skills. The same agent that works with Vibrant and Rupa today will expand to cover additional blood test, urine test, functional medicine, and imaging portals going forward.

Privacy first

Your portal credentials never leave your device. The agent runs a local browser session, we never store your portal password or intercept your session.

Getting started
1. Log into our platform and navigate to Biomarker Profile, Blood Test
2. Download the AI Agent app from the Settings panel
3. Pair the agent with your account using a one-time token
4. Click Explore Portal, the agent takes it from there

We'd love to hear your feedback. Reply to this email or reach out directly, we're actively building based on what clinics need most.

Best,
The Team`,
    },
  },
];

// ── Demo content panel ────────────────────────────────────────────────────────
function DemoPanel({ demo }: { demo: typeof DEMOS[number] }) {
  return (
    <DemoCard>
      <DemoHeader>
        <DemoId>{demo.id}</DemoId>
        <DemoTitle>{demo.label}</DemoTitle>
        <YoutubeLink href={demo.youtubeUrl} target="_blank" rel="noopener noreferrer">
          ▶ YouTube
        </YoutubeLink>
      </DemoHeader>

      <SectionTitle>Assets &amp; Paths</SectionTitle>
      <Grid>
        <InfoBlock>
          <BlockLabel>Total Clips</BlockLabel>
          <BlockValue style={{ fontSize: 28, fontFamily: 'inherit', fontWeight: 700, color: '#1d4ed8' }}>
            {demo.totalClips}
          </BlockValue>
          <ClipGrid>
            {demo.clipNums.map(n => (
              <ClipBadge key={n}>clip {n}</ClipBadge>
            ))}
          </ClipGrid>
        </InfoBlock>

        <InfoBlock>
          <BlockLabel>Script TXT Files</BlockLabel>
          <BlockValue>{demo.scriptsDir}clip1.txt … clip{demo.totalClips}.txt</BlockValue>
          <BlockLabel style={{ marginTop: 14 }}>MP3 Audio Files</BlockLabel>
          <BlockValue>{demo.mp3Dir}clip_01.mp3 … clip_{String(demo.totalClips).padStart(2, '0')}.mp3</BlockValue>
        </InfoBlock>

        <InfoBlock>
          <BlockLabel>Raw Video (iMovie export)</BlockLabel>
          <BlockValue>{demo.rawVideo}</BlockValue>
        </InfoBlock>

        <InfoBlock>
          <BlockLabel>Final Video (captions burned)</BlockLabel>
          <BlockValue>{demo.finalVideo}</BlockValue>
          <BlockLabel style={{ marginTop: 14 }}>Caption Command</BlockLabel>
          <BlockValue>{demo.captionCmd}</BlockValue>
        </InfoBlock>
      </Grid>

      <InfoBlock style={{ marginBottom: 24 }}>
        <BlockLabel>YouTube URL</BlockLabel>
        <CopyRow style={{ marginTop: 6 }}>
          <BlockValue style={{ flex: 1 }}>
            <a href={demo.youtubeUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>
              {demo.youtubeUrl}
            </a>
          </BlockValue>
          <CopyButton text={demo.youtubeUrl} />
        </CopyRow>
      </InfoBlock>

      <Divider />

      <SectionTitle>Redaction</SectionTitle>
      <Grid>
        <InfoBlock>
          <BlockLabel>Annotations JSON</BlockLabel>
          <CopyRow style={{ marginTop: 4 }}>
            <BlockValue style={{ flex: 1 }}>{demo.redact.annotationsFile}</BlockValue>
            <CopyButton text={demo.redact.annotationsFile} />
          </CopyRow>
          <BlockLabel style={{ marginTop: 14 }}>Redacted Video Output</BlockLabel>
          <BlockValue>{demo.redact.redactedVideo}</BlockValue>
        </InfoBlock>

        <InfoBlock>
          <BlockLabel>Open Redactor Tool</BlockLabel>
          <BlockValue style={{ fontFamily: 'inherit', fontSize: 13, marginBottom: 10 }}>
            Draw ROI boxes frame-by-frame, export annotations JSON, then run blur command.
          </BlockValue>
          <a
            href="/video-redactor"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              padding: '7px 16px',
              background: '#1A2744',
              color: '#fff',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 700,
              textDecoration: 'none',
            }}
          >
            Open Video Redactor →
          </a>
        </InfoBlock>
      </Grid>

      <InfoBlock style={{ marginBottom: 24 }}>
        <BlockLabel>Blur Command</BlockLabel>
        <CopyRow style={{ marginTop: 6 }}>
          <BlockValue style={{ flex: 1 }}>{demo.redact.blurCmd}</BlockValue>
          <CopyButton text={demo.redact.blurCmd} />
        </CopyRow>
      </InfoBlock>

      <Divider />

      <SectionTitle>ElevenLabs TTS</SectionTitle>
      <Grid>
        <InfoBlock>
          <BlockLabel>Voice</BlockLabel>
          <BlockValue style={{ fontFamily: 'inherit', fontSize: 14, fontWeight: 600 }}>{demo.tts.voice}</BlockValue>
          <BlockLabel style={{ marginTop: 14 }}>Voice ID</BlockLabel>
          <CopyRow style={{ marginTop: 4 }}>
            <BlockValue style={{ flex: 1 }}>{demo.tts.voiceId}</BlockValue>
            <CopyButton text={demo.tts.voiceId} />
          </CopyRow>
          <BlockLabel style={{ marginTop: 14 }}>Model</BlockLabel>
          <BlockValue>{demo.tts.model}</BlockValue>
        </InfoBlock>

        <InfoBlock>
          <BlockLabel>Voice Settings</BlockLabel>
          <BlockValue>
            stability: {demo.tts.stability}{'\n'}
            similarity_boost: {demo.tts.similarityBoost}{'\n'}
            style: {demo.tts.style}{'\n'}
            speaker_boost: {String(demo.tts.speakerBoost)}
          </BlockValue>
        </InfoBlock>
      </Grid>

      <InfoBlock style={{ marginBottom: 24 }}>
        <BlockLabel>Generate MP3s Command</BlockLabel>
        <CopyRow style={{ marginTop: 6 }}>
          <BlockValue style={{ flex: 1 }}>{demo.tts.generateCmd}</BlockValue>
          <CopyButton text={demo.tts.generateCmd} />
        </CopyRow>
      </InfoBlock>

      <Divider />

      <SectionTitle>Marketing Copy</SectionTitle>

      <FieldLabel>Title</FieldLabel>
      <CopyRow>
        <CopyField>{demo.marketing.title}</CopyField>
        <CopyButton text={demo.marketing.title} />
      </CopyRow>

      <FieldLabel>Short Description</FieldLabel>
      <CopyRow>
        <CopyField>{demo.marketing.shortDesc}</CopyField>
        <CopyButton text={demo.marketing.shortDesc} />
      </CopyRow>

      <FieldLabel>Email Content</FieldLabel>
      <CopyRow>
        <EmailCopyField>{demo.marketing.email}</EmailCopyField>
        <CopyButton text={demo.marketing.email} />
      </CopyRow>
    </DemoCard>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
const VideoDemoLibrary: React.FC = () => {
  const [activeId, setActiveId] = useState(DEMOS[0].id);
  const activeDemo = DEMOS.find(d => d.id === activeId)!;

  return (
    <Page>
      <H1>Video Demo Library</H1>
      <Subtitle>Internal reference — localhost only. All demo details, assets, captions, and marketing copy.</Subtitle>

      <TabBar>
        {DEMOS.map(demo => (
          <Tab key={demo.id} $active={activeId === demo.id} onClick={() => setActiveId(demo.id)}>
            {demo.label}
          </Tab>
        ))}
      </TabBar>

      <DemoPanel demo={activeDemo} />
    </Page>
  );
};

export default VideoDemoLibrary;
